import { useCallback, useEffect, useRef, useState } from 'react';
import usePolling from 'hooks/usePolling';
import useStaticCallback from 'hooks/useStaticCallback';
import { OnOpen, WindowFeatures } from './types';
import { serializeFeatures } from './utils';

const DEFAULT_POLLING_INTERVAL_MILLIS = 500;

export interface UsePopupConfig {
  /**
   * the name of the popup window
   */
  target: string;
  /**
   * called when the window closes by any means - by user, programmatically, etc.
   * the hook installs a poller that continually checks for the window closing
   * and fires this callback when it sees that the window is closed
   */
  onClose?: () => void;
  /**
   * called if there are errors opening the popup window (e.g. if the popup
   * gets blocked by the browser)
   */
  onError?: (error: Error) => void;
  /**
   * called with a reference to the popup window when it opens
   */
  onOpen?: OnOpen;
  /**
   * the URL to load in the popup window.  not specifying this prop will result
   * in the window loading "about:blank"
   */
  url?: string;
  /**
   * parameters for customizing the popup.  Typescript does not provide strong
   * types for these options.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/open for description
   *      of "window features"
   */
  features?: WindowFeatures;
  /**
   * how often to check if the opened window is closed, in milliseconds
   */
  windowPollingIntervalMillis?: number;
}

export interface UsePopupResult {
  close: () => void;
  open: () => void;
  setPopupUrl: (url: string) => void;
}

/**
 * A hook to control a popup window, which could actually be a separate window,
 * separate tab, etc.  The hook returns an `open` function which is a wrapper around
 * window.open
 */
export default function usePopup({
  features,
  target,
  onClose,
  onError,
  onOpen,
  url,
  windowPollingIntervalMillis = DEFAULT_POLLING_INTERVAL_MILLIS,
}: UsePopupConfig): UsePopupResult {
  const popup = useRef<Window | null>(null);
  const [prevUrl, setPrevUrl] = useState<string | undefined>(url);

  // have to call onClose in the unmount effect hook, but don't want it
  // causing the effect hook to rerun
  const staticOnClose = useStaticCallback(onClose);

  const setPopupUrl = useCallback(
    (newUrl: string | undefined) => {
      if (popup.current && newUrl) {
        popup.current.location.href = newUrl;

        if (prevUrl !== newUrl) {
          setPrevUrl(newUrl);
        }
      }
    },
    [prevUrl],
  );

  if (url !== prevUrl) {
    setPopupUrl(url);
  }

  const [pollForClose, cancelPolling] = usePolling(
    (done) => {
      if (popup.current?.closed) {
        popup.current = null;
        onClose?.();
        done();
      }
    },
    { intervalMillis: windowPollingIntervalMillis },
  );

  // need to cancel polling when the component unmounts, but don't want the
  // unmount effect to re-run when cancelPolling changes
  const staticCancelPolling = useStaticCallback(cancelPolling);

  // close the window programmatically.  when this happens, we don't need to wait
  // for the poller to discover that the window is closed to call onClose - the
  // poller can be cancled and onClose can be called here
  const close = useCallback(() => {
    cancelPolling();

    if (popup.current) {
      popup.current.close();
      popup.current = null;
      onClose?.();
    }
  }, [cancelPolling, onClose]);

  // cleanup effect that should be run because the component using this hook is
  // no longer displayed to the user
  useEffect(
    () => () => {
      if (popup.current) {
        staticCancelPolling();
        popup.current.close();
        staticOnClose?.();
      }
    },
    [staticCancelPolling, staticOnClose],
  );

  const open = useCallback(() => {
    const featureString = serializeFeatures(features);

    popup.current = window.open(url, target, featureString);

    if (popup.current) {
      pollForClose();
      onOpen?.(popup.current, close);
    } else {
      onError?.(
        new Error(
          'Popups are disbled. Allow popups for this site and refresh the page.',
        ),
      );
    }
  }, [close, features, onError, onOpen, pollForClose, target, url]);

  return { close, open, setPopupUrl };
}
